import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { ConnectAccountButtonMemo } from '@/components/socialMedia/ConnectAccountButton';
import { ConnectedAccountsListMemo } from '@/components/socialMedia/ConnectedAccountsList';
import { SettingsSubmenuMemo } from '@/components/settings/SettingsSubmenu';
import { TelegramPersonalConnect } from '@/components/settings/TelegramPersonalConnect';
import { GmailConnect } from '@/components/settings/GmailConnect';
import { WhatsAppConnect } from '@/components/settings/WhatsAppConnect';
import type { IConnectedSocialAccount } from '@/@types/socialMedia';

const ConnectedAccountsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get query parameters for OAuth callback feedback
  const oauthSuccess = searchParams.get('success');
  const oauthError = searchParams.get('error');
  const pagesConnected = searchParams.get('pages');

  // Fetch connected accounts
  const accounts = useQuery(api.socialMedia.accounts.listConnectedAccounts);
  const disconnectAccount = useMutation(api.socialMedia.accounts.disconnectAccount);

  // Derived state
  const isLoading = accounts === undefined;
  const accountsList = useMemo(() => accounts || [], [accounts]);
  const hasAccounts = accountsList.length > 0;

  // Show success/error toast notifications
  useEffect(() => {
    if (oauthSuccess === 'facebook' && pagesConnected) {
      const count = parseInt(pagesConnected, 10);
      const message =
        count === 1
          ? 'Successfully connected 1 Facebook Page'
          : `Successfully connected ${count} Facebook Pages`;

      toast.success(message);

      // Clean up URL
      navigate('/connected-accounts', { replace: true });
    }

    if (oauthError) {
      const message = `Failed to connect: ${decodeURIComponent(oauthError)}`;

      toast.error(message);

      // Clean up URL
      navigate('/connected-accounts', { replace: true });
    }
  }, [oauthSuccess, oauthError, pagesConnected, navigate]);

  // Handle account disconnection
  const handleDisconnect = useCallback(
    async (accountId: Id<'connectedSocialAccounts'>) => {
      const confirmed = window.confirm(
        'Are you sure you want to disconnect this account? You will need to re-authorize to publish posts.'
      );

      if (!confirmed) {
        return;
      }

      await disconnectAccount({ accountId });
    },
    [disconnectAccount]
  );

  // Handle reconnection (triggers OAuth flow again)
  const handleReconnect = useCallback(() => {
    // For reconnection, we can reuse the same OAuth flow
    // The backend will update the existing account instead of creating a new one
    window.location.reload();
  }, []);

  const mainContent = (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Connected Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your social media accounts for publishing AI-generated posts
          </p>
        </div>

        <ConnectAccountButtonMemo platform="facebook_page" />
      </div>

      {/* Stats Summary */}
      {hasAccounts && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-card border rounded-lg p-4">
            <div className="text-sm font-medium text-muted-foreground">Total Accounts</div>
            <div className="text-2xl font-bold text-foreground mt-1">{accountsList.length}</div>
          </div>

          <div className="bg-card border rounded-lg p-4">
            <div className="text-sm font-medium text-muted-foreground">Active Accounts</div>
            <div className="text-2xl font-bold text-foreground mt-1">
              {accountsList.filter((a: IConnectedSocialAccount) => a.status === 'active').length}
            </div>
          </div>

          <div className="bg-card border rounded-lg p-4">
            <div className="text-sm font-medium text-muted-foreground">Total Posts Published</div>
            <div className="text-2xl font-bold text-foreground mt-1">
              {accountsList.reduce(
                (sum: number, account: IConnectedSocialAccount) =>
                  sum + account.totalPublishedPosts,
                0
              )}
            </div>
          </div>
        </div>
      )}

      {/* Connected Accounts List */}
      <div className="bg-card border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Your Accounts</h2>

        <ConnectedAccountsListMemo
          accounts={accountsList}
          isLoading={isLoading}
          onDisconnect={handleDisconnect}
          onReconnect={handleReconnect}
        />
      </div>

      {/* Messaging Accounts */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-foreground mb-4">Messaging</h2>
        <div className="flex flex-col gap-4">
          <TelegramPersonalConnect />
          <GmailConnect />
          <WhatsAppConnect />
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-8 bg-card border border-border rounded-lg p-6">
        <h3 className="text-sm font-semibold text-foreground mb-2">About Connected Accounts</h3>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li>• Connect your Facebook Pages to publish AI-generated posts directly</li>
          <li>• Page access tokens never expire if you are a page admin</li>
          <li>• You can disconnect and reconnect accounts at any time</li>
          <li>
            • Expired or invalid accounts need to be reconnected before you can publish to them
          </li>
        </ul>
      </div>
    </div>
  );

  return {
    submenu: <SettingsSubmenuMemo />,
    mainContent,
  };
};

export default ConnectedAccountsPage;
